"use client";
import { useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Chat, Message } from "../types";

export function useChatSubscriptions() {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const messageChannelsRef = useRef<{ [chatId: string]: any }>({});
  const processedMessageIdsRef = useRef<Set<string>>(new Set());

  const setupChatMessageSubscription = useCallback(
    (
      chatId: string,
      onNewMessage: (message: Message) => void,
      isMounted: React.MutableRefObject<boolean>
    ) => {
      if (!chatId || chatId === "default") {
        return () => {};
      }

      // Clean up existing subscription if any
      if (messageChannelsRef.current[chatId]) {
        supabase.removeChannel(messageChannelsRef.current[chatId]);
        delete messageChannelsRef.current[chatId];
      }
      const channel = supabase
        .channel(`chat-${chatId}-messages-instant`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "messages",
            filter: `chat_id=eq.${chatId}`,
          },
          (payload) => {
            if (payload.new && isMounted.current) {
              const messageId = payload.new.id;

              if (processedMessageIdsRef.current.has(messageId)) {
                return;
              }

              processedMessageIdsRef.current.add(messageId);
              if (processedMessageIdsRef.current.size > 1000) {
                const iterator = processedMessageIdsRef.current.values();
                for (let i = 0; i < 200; i++) {
                  processedMessageIdsRef.current.delete(
                    iterator.next().value as string
                  );
                }
              }

              const newMessage = {
                id: messageId,
                text: payload.new.text,
                sender: payload.new.sender as "user" | "team",
                timestamp: new Date(payload.new.timestamp).getTime(),
                isRead: payload.new.is_read,
              };

              onNewMessage(newMessage);
            }
          }
        )
        .subscribe((status) => {});

      messageChannelsRef.current[chatId] = channel;

      return () => {
        if (messageChannelsRef.current[chatId]) {
          supabase.removeChannel(messageChannelsRef.current[chatId]);
          delete messageChannelsRef.current[chatId];
        }
      };
    },
    []
  );

  const setupRealtimeSubscription = useCallback(
    (
      onChatChange: () => void,
      onMessageInsert: (chatId: string, message: any) => void,
      isMounted: React.MutableRefObject<boolean>,
      isRefreshingRef: React.MutableRefObject<boolean>,
      chats: Chat[]
    ) => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const channel = supabase
        .channel("schema-db-changes")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "chats",
          },
          (payload) => {
            console.log("[CHAT SUBSCRIPTIONS] Chat change detected:", payload);
            if (isMounted.current && !isRefreshingRef.current) {
              onChatChange();
            }
          }
        )
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "messages",
          },
          (payload) => {
            if (
              payload.eventType === "INSERT" &&
              payload.new &&
              payload.new.chat_id
            ) {
              const chatId = payload.new.chat_id;

              const chat = chats.find((chat) => chat.id === chatId);
              if (
                chat &&
                !chat.status?.startsWith("closed") &&
                isMounted.current
              ) {
                onMessageInsert(chatId, payload.new);
              } else {
                if (!isRefreshingRef.current && isMounted.current) {
                  onChatChange();
                }
              }
            }
          }
        )
        .subscribe((status) => {
          if (!isMounted.current) return;
          if (status === "SUBSCRIBED") {
          } else if (status === "CHANNEL_ERROR") {
            setTimeout(
              () =>
                setupRealtimeSubscription(
                  onChatChange,
                  onMessageInsert,
                  isMounted,
                  isRefreshingRef,
                  chats
                ),
              5000
            );
          }
        });

      channelRef.current = channel;

      return () => {
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
      };
    },
    []
  );

  /**
   * Clean up all subscriptions
   */
  const cleanupAllSubscriptions = useCallback(() => {
    // Clean up main channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Clean up all message channels
    Object.values(messageChannelsRef.current).forEach((channel) => {
      supabase.removeChannel(channel);
    });
    messageChannelsRef.current = {};
  }, []);

  return {
    setupChatMessageSubscription,
    setupRealtimeSubscription,
    cleanupAllSubscriptions,
    processedMessageIdsRef,
  };
}
